import React from "react";

import { useTranslation } from "react-i18next";

import "../../../css/admin/markdown-editor.scss";
import SimpleMDEEditor from "../../form/SimpleMDEEditor";

import type { ChangeEvent, JSX } from "react";

import type { TranslationsIndexes } from "../../../types";

interface BaseTranslation {
  language: string;
  default: boolean;
}

interface ItemWithTranslations<T extends BaseTranslation> {
  translations: T[];
  translations_indexes: TranslationsIndexes;
}

interface TranslateFormDoubleInputProps<T extends BaseTranslation> {
  item: ItemWithTranslations<T>;
  title?: string;
  fieldName: string;
  subFieldList: {
    key: string;
    label: string;
    type?: "text" | "textarea" | "markdown";
    maxLength?: number;
  }[];
  onChange: (translations: T[]) => void;
}

function getNestedValue(object: BaseTranslation, key: string): unknown {
  const objectRecord = object as unknown as Record<string, unknown>;
  const arrayMatch = /^(\w+)\[(\d+)\]$/.exec(key);
  if (arrayMatch) {
    const fieldName = arrayMatch[1];
    const index = arrayMatch[2];
    if (fieldName && index) {
      const arrayField = objectRecord[fieldName] as unknown[];
      return arrayField?.[parseInt(index, 10)];
    }
  }

  return objectRecord[key];
}

function setNestedValue(
  object: BaseTranslation,
  key: string,
  value: unknown,
): BaseTranslation {
  const objectRecord = object as unknown as Record<string, unknown>;
  const newObject = { ...objectRecord };

  const arrayMatch = /^(\w+)\[(\d+)\]$/.exec(key);
  if (arrayMatch) {
    const fieldName = arrayMatch[1];
    const index = arrayMatch[2];

    if (fieldName && index) {
      const arrayIndex = parseInt(index, 10);

      if (!newObject[fieldName]) {
        newObject[fieldName] = [];
      } else {
        newObject[fieldName] = [...(newObject[fieldName] as unknown[])];
      }

      (newObject[fieldName] as unknown[])[arrayIndex] = value;
      return newObject as unknown as BaseTranslation;
    }
  }

  newObject[key] = value;
  return newObject as unknown as BaseTranslation;
}
function isTranslation<T extends BaseTranslation>(value: unknown): value is T {
  return (
    typeof value === "object" &&
    value !== null &&
    "language" in value &&
    "default" in value
  );
}

function isObjectWithStringKeys(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export default function TranslateFormDoubleInput<T extends BaseTranslation>({
  item,
  title,
  fieldName,
  subFieldList,
  onChange,
}: TranslateFormDoubleInputProps<T>): JSX.Element {
  const { t } = useTranslation("languages");

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string,
    translationIndex: number,
  ): void => {
    const { value } = event.target;

    const updatedTranslations = [...item.translations];
    const currentTranslation = updatedTranslations[translationIndex];

    if (!currentTranslation || !isTranslation(currentTranslation)) {
      console.error("Current translation is not valid:", currentTranslation);
      return;
    }

    const currentFieldValue = getNestedValue(currentTranslation, fieldName);

    if (isObjectWithStringKeys(currentFieldValue)) {
      const updatedFieldValue = {
        ...currentFieldValue,
        [key]: value,
      };

      const updatedTranslation = setNestedValue(
        currentTranslation,
        fieldName,
        updatedFieldValue,
      );

      if (!isTranslation<T>(updatedTranslation)) {
        console.error("Updated translation is not valid:", updatedTranslation);
        return;
      }

      updatedTranslations[translationIndex] = updatedTranslation;
    }

    onChange(updatedTranslations);
  };

  const getFieldValue = (translation: T, key: string): string => {
    const fieldValue = getNestedValue(translation, fieldName);

    if (typeof fieldValue === "string") {
      return fieldValue;
    }

    if (isObjectWithStringKeys(fieldValue)) {
      const value = fieldValue[key];
      return typeof value === "string" ? value : "";
    }

    return "";
  };

  const renderField = (
    type: "text" | "textarea" | "markdown" | undefined,
    translation: T,
    key: string,
    index: number,
    maxLength?: number,
  ): JSX.Element => {
    const fieldValue = getFieldValue(translation, key);

    switch (type) {
      case "textarea":
        return (
          <textarea
            className="border-border-strong w-full rounded-[0.234375rem] border px-[0.625rem] py-[0.3125rem]"
            cols={50}
            maxLength={maxLength}
            rows={4}
            value={fieldValue}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>): void =>
              handleChange(event, key, index)
            }
          />
        );

      case "markdown":
        return (
          <SimpleMDEEditor
            className="w-full"
            value={fieldValue}
            onChange={(value: string): void => {
              // We create a synthetic event to reuse handleChange
              const syntheticEvent = {
                target: { value },
              } as ChangeEvent<HTMLInputElement>;
              handleChange(syntheticEvent, key, index);
            }}
            options={{
              spellChecker: false,
              status: false,
              hideIcons: ["guide", "fullscreen", "side-by-side"],
            }}
          />
        );

      case "text":
      default:
        return (
          <input
            className="border-border-strong w-full rounded-[0.234375rem] border px-[0.625rem] py-[0.3125rem]"
            maxLength={maxLength}
            type="text"
            value={fieldValue}
            onChange={(event: ChangeEvent<HTMLInputElement>): void =>
              handleChange(event, key, index)
            }
          />
        );
    }
  };

  return (
    <div>
      {title ? (
        <h3 className="mb-[0.625rem] py-[0.3125rem] text-[0.9375rem] font-semibold">
          {title}
        </h3>
      ) : null}
      <div className="flex flex-col gap-[1.5625rem] px-4! py-2!">
        {item.translations.map((translation, index) => (
          <div
            key={`${String(fieldName)}-${translation.language}`}
            className="flex gap-[2.5rem]"
          >
            <label
              className="text-muted-foreground w-[3.75rem] shrink-0 font-normal italic"
              htmlFor={`${String(fieldName)}-${translation.language}`}
            >
              {t(`languages:langs.${translation.language}`)}
              {translation.default ? "*" : null}
            </label>
            <div className="mx-[0.625rem] flex w-full flex-col gap-[0.46875rem] overflow-hidden">
              {subFieldList.map(({ key, label, type, maxLength }) => (
                <div key={key} className="flex items-center gap-[0.46875rem]">
                  <div className="flex w-[4.375rem] shrink-0 flex-col">
                    <label
                      className="mb-0 leading-tight font-normal"
                      htmlFor={`${fieldName}-${translation.language}-${key}`}
                    >
                      {label}
                    </label>
                    {type === "textarea" && maxLength ? (
                      <span className="text-muted-foreground text-right text-[0.625rem]">
                        {getFieldValue(translation, key).length} / {maxLength}
                      </span>
                    ) : null}
                  </div>
                  <div className="w-full overflow-hidden">
                    {renderField(type, translation, key, index, maxLength)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
